import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  Chip,
  CircularProgress,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
} from '@mui/material';
import Editor from '@monaco-editor/react';
import { api } from '../services/api';
import LastKnownGoodMenu from './LastKnownGoodMenu';

const BulkConfiguration = ({ devices, onDevicesUpdate }) => {
  // Normalize devices to always be an array
  const devicesArray = devices || [];

  const [selectedDevices, setSelectedDevices] = useState(() => {
    // Initialize from localStorage or empty array
    const saved = localStorage.getItem('selectedDevices');
    return saved ? JSON.parse(saved) : [];
  });
  const [configJson, setConfigJson] = useState('');
  const [isJsonValid, setIsJsonValid] = useState(true);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [results, setResults] = useState([]);
  const [mode, setMode] = useState('fields'); // 'fields' | 'raw'
  const [fieldPatch, setFieldPatch] = useState({
    band: '',
    channel: '',
    htmode: '',
    tx_power_up: '',
    tx_power_down: '',
    max_children: '',
  });

  // Helper function to get device types (consistent with Commands tab)
  const getDeviceType = (capabilities) => {
    if (!capabilities) {
      return 'N/A';
    }
    if (typeof capabilities === 'object') {
      const model = capabilities.model;
      if (model) {
        const modelLower = model.toLowerCase();
        if (modelLower.includes('juganucap')) {
          return 'CAP';
        } else if (modelLower.includes('juganunode')) {
          return 'Node';
        } else if (modelLower.includes('nvidia') || modelLower.includes('qrb5165-128gb')) {
          return 'AI';
        } else if (modelLower.includes('edgecore') || modelLower.includes('eap102')) {
          return 'EdgeCore';
        }
      }
      // Also check compatible field for EdgeCore devices
      if (capabilities.compatible === 'edgecore_eap102') {
        return 'EdgeCore';
      }
    }
    return 'N/A';
  };

  // Save to localStorage whenever selectedDevices changes
  useEffect(() => {
    localStorage.setItem('selectedDevices', JSON.stringify(selectedDevices));
  }, [selectedDevices]);

  const handleDeviceChange = (event) => {
    const value = event.target.value;
    const selectedValues = typeof value === 'string' ? value.split(',') : value;
    
    // Check if CLEAR_ALL is selected
    if (selectedValues.includes('CLEAR_ALL')) {
      setSelectedDevices([]);
      return;
    }
    
    // Handle special bulk selection values
    let finalDevices = [];
    selectedValues.forEach(val => {
      if (val === 'ALL_DEVICES') {
        finalDevices = [...finalDevices, ...devicesArray.map(d => d.serial_number)];
      } else if (val === 'ALL_NODES') {
        finalDevices = [...finalDevices, ...devicesArray.filter(d => getDeviceType(d.capabilities) === 'Node').map(d => d.serial_number)];
      } else if (val === 'ALL_CAPS') {
        finalDevices = [...finalDevices, ...devicesArray.filter(d => getDeviceType(d.capabilities) === 'CAP').map(d => d.serial_number)];
      } else if (val === 'ALL_AI') {
        finalDevices = [...finalDevices, ...devicesArray.filter(d => getDeviceType(d.capabilities) === 'AI').map(d => d.serial_number)];
      } else if (val === 'ALL_EDGECORE') {
        finalDevices = [...finalDevices, ...devicesArray.filter(d => getDeviceType(d.capabilities) === 'EdgeCore').map(d => d.serial_number)];
      } else {
        // Regular device serial number
        finalDevices.push(val);
      }
    });
    
    // Remove duplicates
    const uniqueDevices = [...new Set(finalDevices)];
    setSelectedDevices(uniqueDevices);
  };

  const handleSaveConfig = async () => {
    try {
      let parsedConfig;
      try {
        parsedConfig = JSON.parse(configJson);
      } catch (e) {
        setStatus({ type: 'error', message: 'Invalid JSON format' });
        return;
      }

      setLoading(true);
      setResults([]);
      
      // Determine which devices to configure based on selection
      // Since selectedDevices now contains actual device serial numbers (bulk selections are expanded)
      const devicesToConfigure = devicesArray.filter(d => selectedDevices.includes(d.serial_number));

      if (devicesToConfigure.length === 0) {
        setStatus({ type: 'warning', message: 'No devices selected for configuration' });
        setLoading(false);
        return;
      }

      const serials = devicesToConfigure.map((d) => d.serial_number);
      const resp = await api.applyDeviceConfigBatch({
        serialNumbers: serials,
        configuration: parsedConfig,
      });
      const resultBySerial = new Map(
        (resp?.results || []).map((row) => [row.serial?.toLowerCase(), row]),
      );
      const results = devicesToConfigure.map((device) => {
        const row = resultBySerial.get(device.serial_number?.toLowerCase());
        if (!row) {
          return {
            serial: device.serial_number,
            success: false,
            message: 'No result returned',
          };
        }
        if (!row.success) {
          return {
            serial: device.serial_number,
            success: false,
            message: row.error || row.message || 'Failed to save configuration',
          };
        }
        const sendFailed = row.error === 'Failed to send command to device'
          || row.message === 'Failed to send command to device';
        return {
          serial: device.serial_number,
          success: true,
          message: sendFailed
            ? 'Configuration saved but not yet applied'
            : 'Configuration saved successfully',
        };
      });

      setResults(results);
      setStatus({ 
        type: 'success', 
        message: `Configuration process completed for ${devicesToConfigure.length} device(s)` 
      });
      
      // Refresh device list
      onDevicesUpdate();
    } catch (err) {
      setStatus({ type: 'error', message: err.message || 'Failed to save configuration' });
    } finally {
      setLoading(false);
    }
  };

  const handleEditorChange = (value) => {
    setConfigJson(value);
    try {
      JSON.parse(value);
      setIsJsonValid(true);
      setStatus({ type: '', message: '' });
    } catch (e) {
      setIsJsonValid(false);
    }
  };

  const setFieldPatchValue = (field, value) => {
    setFieldPatch((prev) => ({ ...prev, [field]: value }));
    setStatus({ type: '', message: '' });
  };

  const fieldPatchHasChanges = Object.values(fieldPatch).some((v) => v !== '' && v !== undefined && v !== null);

  const buildBulkFieldPatch = () => {
    // Bulk patch is intentionally per-tree-id-less; the BE applies it to the
    // single treemgmt entry on Node devices and rejects on CAPs (where users
    // need to use per-device editing). Send an empty tree_id; the BE will fall
    // back to the sole entry on Nodes.
    const entry = { tree_id: '' };
    if (fieldPatch.band) entry.band = fieldPatch.band;
    if (fieldPatch.htmode) entry.htmode = fieldPatch.htmode;
    if (fieldPatch.channel !== '') entry.channel = parseInt(fieldPatch.channel, 10);
    if (fieldPatch.tx_power_up !== '') entry.tx_power_up = parseInt(fieldPatch.tx_power_up, 10);
    if (fieldPatch.tx_power_down !== '') entry.tx_power_down = parseInt(fieldPatch.tx_power_down, 10);
    if (fieldPatch.max_children !== '') entry.max_children = parseInt(fieldPatch.max_children, 10);
    return { trees: [entry] };
  };

  const handleApplyFieldPatch = async () => {
    const devicesToConfigure = devicesArray.filter((d) => selectedDevices.includes(d.serial_number));
    if (devicesToConfigure.length === 0) {
      setStatus({ type: 'warning', message: 'No devices selected for configuration' });
      return;
    }
    if (!fieldPatchHasChanges) {
      setStatus({ type: 'warning', message: 'No field changes to apply' });
      return;
    }
    setLoading(true);
    setResults([]);
    try {
      const resp = await api.bulkPatchTreeMgmtConfig(
        devicesToConfigure.map((d) => d.serial_number),
        buildBulkFieldPatch()
      );
      const apiResults = (resp && resp.results) || [];
      setResults(apiResults.map((r) => ({
        serial: r.serial,
        success: r.success,
        message: r.message || (r.success ? 'Tree-management settings applied' : 'Failed'),
      })));
      const ok = apiResults.filter((r) => r.success).length;
      setStatus({
        type: ok === apiResults.length ? 'success' : 'warning',
        message: `Tree-management patch applied to ${ok}/${apiResults.length} device(s)`,
      });
      onDevicesUpdate();
    } catch (err) {
      setStatus({ type: 'error', message: err.message || 'Failed to apply tree-management patch' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 2 }}>
          <Box>
            <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
              Bulk Device Configuration
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Last known good applies to jnet2 CAP and Node devices only.
            </Typography>
          </Box>
          <LastKnownGoodMenu
            scope="all"
            buttonSx={{
              border: '1px solid',
              borderColor: 'divider',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: 'action.hover',
              },
            }}
            onComplete={({ type, message }) => {
              setStatus({ type, message });
              if (type === 'success') {
                onDevicesUpdate();
              }
            }}
          />
        </Box>

        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Select Devices</InputLabel>
          <Select
            multiple
            value={selectedDevices}
            onChange={handleDeviceChange}
            onOpen={async () => {
              // Refresh devices when opening the dropdown
              try {
                const response = await api.getDevices({ fields: ['core'] });
                // Handle new response format with devices array
                const devicesArray = response?.devices || (Array.isArray(response) ? response : []);
                onDevicesUpdate(devicesArray);
              } catch (error) {
                console.error('Error refreshing devices:', error);
              }
            }}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 300
                }
              },
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left'
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left'
              },
              getContentAnchorEl: null
            }}
            onClose={() => {
              // Force a re-render of the menu items
              setSelectedDevices(prev => [...prev]);
            }}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => {
                  const device = devicesArray.find(d => d.serial_number === value);
                  return (
                    <Chip
                      key={value}
                      label={device?.name || value}
                      color={device?.connected ? 'success' : 'error'}
                      size="small"
                      onDelete={() => {
                        setSelectedDevices(prev => prev.filter(id => id !== value));
                      }}
                      onMouseDown={(event) => {
                        event.stopPropagation();
                      }}
                    />
                  );
                })}
              </Box>
            )}
          >
            {/* Bulk Selection Options */}
            <MenuItem value="ALL_DEVICES" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              📱 All Devices ({devicesArray.length})
            </MenuItem>
            <MenuItem value="ALL_NODES" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              🔗 All Nodes ({devicesArray.filter(d => getDeviceType(d.capabilities) === 'Node').length})
            </MenuItem>
            <MenuItem value="ALL_CAPS" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              📡 All CAPs ({devicesArray.filter(d => getDeviceType(d.capabilities) === 'CAP').length})
            </MenuItem>
            <MenuItem value="ALL_AI" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              🤖 All AI ({devicesArray.filter(d => getDeviceType(d.capabilities) === 'AI').length})
            </MenuItem>
            <MenuItem value="ALL_EDGECORE" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              🏢 All EdgeCore ({devicesArray.filter(d => getDeviceType(d.capabilities) === 'EdgeCore').length})
            </MenuItem>
            <MenuItem value="CLEAR_ALL" sx={{ fontWeight: 'bold', color: 'secondary.main' }}>
              🗑️ Clear All
            </MenuItem>
            <Divider />
            {/* Individual Devices */}
            {devicesArray.map((device) => (
              <MenuItem key={device.serial_number} value={device.serial_number}>
                {device.name || device.serial_number} ({getDeviceType(device.capabilities)} - {device.connected ? 'Connected' : 'Disconnected'})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={mode}
            onChange={(_, v) => { if (v) { setMode(v); setStatus({ type: '', message: '' }); } }}
          >
            <ToggleButton value="fields">Tree-Mgmt Fields</ToggleButton>
            <ToggleButton value="raw">Raw JSON</ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {mode === 'fields'
              ? 'Apply a partial tree-management update across the selected devices. Public WiFi is preserved.'
              : 'Replace the entire device configuration JSON (including Public WiFi).'}
          </Typography>
        </Box>

        {mode === 'fields' ? (
          <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Band</InputLabel>
              <Select
                label="Band"
                value={fieldPatch.band}
                onChange={(e) => setFieldPatchValue('band', e.target.value)}
              >
                <MenuItem value=""><em>Unchanged</em></MenuItem>
                <MenuItem value="5GHz">5GHz</MenuItem>
                <MenuItem value="6GHz">6GHz</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Channel"
              type="number"
              sx={{ width: 120 }}
              value={fieldPatch.channel}
              onChange={(e) => setFieldPatchValue('channel', e.target.value)}
            />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>HT Mode</InputLabel>
              <Select
                label="HT Mode"
                value={fieldPatch.htmode}
                onChange={(e) => setFieldPatchValue('htmode', e.target.value)}
              >
                <MenuItem value=""><em>Unchanged</em></MenuItem>
                <MenuItem value="HT20">HT20</MenuItem>
                <MenuItem value="HT40">HT40</MenuItem>
                <MenuItem value="HT80">HT80</MenuItem>
                <MenuItem value="HT160">HT160</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="TX Power Up"
              type="number"
              sx={{ width: 140 }}
              value={fieldPatch.tx_power_up}
              onChange={(e) => setFieldPatchValue('tx_power_up', e.target.value)}
            />
            <TextField
              size="small"
              label="TX Power Down"
              type="number"
              sx={{ width: 150 }}
              value={fieldPatch.tx_power_down}
              onChange={(e) => setFieldPatchValue('tx_power_down', e.target.value)}
            />
            <TextField
              size="small"
              label="Max Children"
              type="number"
              sx={{ width: 140 }}
              value={fieldPatch.max_children}
              onChange={(e) => setFieldPatchValue('max_children', e.target.value)}
            />
          </Box>
        ) : (
          <Box sx={{ height: '400px', border: '1px solid #ccc', borderRadius: 1, mb: 3 }}>
            <Editor
              height="100%"
              defaultLanguage="json"
              value={configJson}
              onChange={handleEditorChange}
              options={{
                minimap: { enabled: false },
                fontSize: 14,
                wordWrap: 'on',
                lineNumbers: 'on',
                scrollBeyondLastLine: false,
                automaticLayout: true,
              }}
            />
          </Box>
        )}

        {status.message && (
          <Alert severity={status.type} sx={{ mb: 2 }}>
            {status.message}
          </Alert>
        )}

        {results.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Results:
            </Typography>
            {results.map((result, index) => (
              <Alert 
                key={index} 
                severity={result.success ? 'success' : 'error'} 
                sx={{ mb: 1 }}
              >
                {result.serial}: {result.message}
              </Alert>
            ))}
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          {mode === 'fields' ? (
            <Button
              variant="contained"
              color="primary"
              onClick={handleApplyFieldPatch}
              disabled={!fieldPatchHasChanges || selectedDevices.length === 0 || loading}
            >
              {loading ? (
                <>
                  <CircularProgress size={24} sx={{ mr: 1 }} />
                  Applying...
                </>
              ) : (
                'Apply Tree-Mgmt Fields'
              )}
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveConfig}
              disabled={!isJsonValid || selectedDevices.length === 0 || loading}
            >
              {loading ? (
                <>
                  <CircularProgress size={24} sx={{ mr: 1 }} />
                  Saving Configuration...
                </>
              ) : (
                'Save Configuration'
              )}
            </Button>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default BulkConfiguration; 